import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { formatEuro, formatDecorrenza, formatDataIt } from '../../lib/calcoli'
import { isAutocarriIntermediari } from '../../lib/validazioni'

// Versione corrente dell'informativa privacy mostrata nello step di invio.
// Bump del numero ogni volta che cambia il testo per audit trail (mostra al
// dealer un'eventuale "policy aggiornata, riconfermare").
const PRIVACY_VERSION = '1.0'

export default function StepInvio({ data, update, praticaId, persist, tariffe, onCompleted }) {
  const navigate = useNavigate()
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)
  const [consensoPrivacy, setConsensoPrivacy] = useState(!!data.dati_comuni?.consenso_privacy)

  const isSoloStandalone = data.tipo_operazione === 'solo_standalone'
  const isAutocarri = isAutocarriIntermediari(data.tipo_operazione)
  const compagniaScelta = tariffe.compagnie.find(c => c.codice === data.scelta_compagnia)
  const preventivo = data.preventivi[data.scelta_compagnia]

  async function inviaPratica() {
    setError(null)
    setSending(true)

    // Validazione Data di decorrenza polizza: obbligatoria, da oggi a +7 giorni.
    const ddRaw = data.dati_comuni?.data_decorrenza
    const dd = ddRaw ? new Date(ddRaw) : null
    const oggi = new Date(); oggi.setHours(0, 0, 0, 0)
    const maxDd = new Date(oggi.getTime() + 7 * 24 * 60 * 60 * 1000)
    if (!dd || isNaN(dd.getTime())) {
      setError('Seleziona la Data di decorrenza della polizza nello step "Documenti".')
      setSending(false); return
    }
    dd.setHours(0, 0, 0, 0)
    if (dd < oggi || dd > maxDd) {
      setError('La Data di decorrenza deve essere compresa tra oggi e 7 giorni da oggi.')
      setSending(false); return
    }

    // Salva il consenso privacy nello state della pratica PRIMA dell'invio
    // (così viene persistito e finisce nella mail/audit).
    const consensoAt = new Date().toISOString()
    if (typeof update === 'function') {
      update({
        dati_comuni: {
          ...data.dati_comuni,
          consenso_privacy: true,
          consenso_privacy_at: consensoAt,
          consenso_privacy_version: PRIVACY_VERSION,
        },
      })
    }

    // 1. Persisti la pratica (resta in DB; lo stato verra' aggiornato dall'API)
    const persisted = await persist({
      dati_comuni: {
        ...data.dati_comuni,
        consenso_privacy: true,
        consenso_privacy_at: consensoAt,
        consenso_privacy_version: PRIVACY_VERSION,
      },
    })
    if (!persisted) { setSending(false); return }

    // 2. Compone FormData con allegati + payload JSON
    const fd = new FormData()
    const payload = {
      pratica_id: persisted.id,
      numero_pratica: persisted.numero_pratica,
      tipo_operazione: data.tipo_operazione,
      cliente_nome: data.cliente_nome,
      cliente_cognome: data.cliente_cognome,
      dati_comuni: data.dati_comuni,
      compagnia_scelta: { codice: data.scelta_compagnia, nome: compagniaScelta?.nome },
      preventivo,
      preventivi_comparati: data.preventivi,
      standalone_selezionati: data.standalone_selezionati,
      totale_finale: data.totale_finale,
    }
    fd.append('payload', JSON.stringify(payload))

    const allegati = data._allegati || {}
    for (const [codice, file] of Object.entries(allegati)) {
      fd.append(`allegati[${codice}]`, file, file.name)
    }

    // 3. Invio alla Supabase Edge Function 'invia-pratica'.
    //    `supabase.functions.invoke` gestisce automaticamente:
    //    - URL della function (derivato da REACT_APP_SUPABASE_URL)
    //    - Header Authorization Bearer con il JWT della sessione corrente
    //    - Serializzazione/deserializzazione del body
    let invokeRes
    try {
      invokeRes = await supabase.functions.invoke('invia-pratica', { body: fd })
    } catch (e) {
      setError('Errore di rete: ' + e.message)
      setSending(false)
      return
    }
    if (invokeRes.error) {
      // Il messaggio dettagliato sta dentro la response della function
      let msg = invokeRes.error.message || 'Invio fallito'
      try {
        const errBody = await invokeRes.error.context?.json?.()
        if (errBody?.error) msg = errBody.error
      } catch (_e) { /* ignore */ }
      setError(`Invio fallito: ${msg}`)
      setSending(false)
      return
    }

    // 5. Successo: la pratica resta in DB con stato 'in_processamento'.
    //    L'admin la processera' (approva/rifiutera') e da li' partira' la mail al dealer.
    //    Reindirizziamo alla pagina di conferma che scarica anche il PDF riassuntivo.
    setDone(true)
    setSending(false)
    const stateForRedirect = {
      data: {
        tipo_operazione: data.tipo_operazione,
        cliente_nome: data.cliente_nome,
        cliente_cognome: data.cliente_cognome,
        dati_comuni: {
          ...data.dati_comuni,
          consenso_privacy: true,
          consenso_privacy_at: consensoAt,
          consenso_privacy_version: PRIVACY_VERSION,
        },
        created_at: persisted.created_at || data.created_at || null,
        compagnie_selezionate: data.compagnie_selezionate,
        preventivi: data.preventivi,
        standalone_selezionati: data.standalone_selezionati,
        scelta_compagnia: data.scelta_compagnia,
        totale_finale: data.totale_finale,
      },
      numeroPratica: persisted.numero_pratica,
      compagniaScelta,
      tariffe,
    }
    navigate(`/pratiche/${persisted.id}/inviata`, { state: stateForRedirect, replace: true })
  }

  if (done) {
    return (
      <div className="alert alert-success">
        ✓ Pratica inviata correttamente. Reindirizzamento alla pagina di conferma…
      </div>
    )
  }

  const allegati = data._allegati || {}
  const numAllegati = Object.keys(allegati).length

  return (
    <div>
      <div className="info-box mt-1" style={{ marginBottom: '1.5rem' }}>
        Riepilogo finale. Al click su "Invia" la pratica viene trasmessa via email all'amministrazione
        per essere processata. Nel frattempo resta visibile nelle tue pratiche con stato <strong>in processamento</strong>.
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="sl">Cliente</div><div className="sv">{data.cliente_cognome} {data.cliente_nome}</div></div>
        {isSoloStandalone ? (
          <>
            <div className="stat-card"><div className="sl">Tipo</div><div className="sv">Solo Stand-alone</div></div>
            <div className="stat-card"><div className="sl">Stand-alone</div><div className="sv">{(data.standalone_selezionati || []).length}</div></div>
          </>
        ) : (
          <>
            <div className="stat-card"><div className="sl">Provincia</div><div className="sv">{data.dati_comuni.provincia}</div></div>
            <div className="stat-card"><div className="sl">Durata</div><div className="sv">{data.dati_comuni.durata_mesi} mesi</div></div>
            <div className="stat-card"><div className="sl">Pacchetto</div><div className="sv">{isAutocarri ? data.dati_comuni.pacchetto_autocarro : data.dati_comuni.pacchetto}</div></div>
            <div className="stat-card"><div className="sl">Compagnia</div><div className="sv">{isAutocarri ? 'Polizze autocarri' : compagniaScelta?.nome}</div></div>
          </>
        )}
        <div className="stat-card"><div className="sl">Allegati</div><div className="sv">{numAllegati}</div></div>
      </div>

      <div className="total-box purple">
        <div className="tl">Totale pratica</div>
        <div className="tv">{formatEuro(data.totale_finale)}</div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Scadenza preventivo (immissione + 30gg) + Data di decorrenza polizza */}
      <div className="info-box mt-1" style={{ marginTop: '1rem' }}>
        La presente quotazione è valida <strong>30 giorni</strong> a partire dalla data di emmissione del preventivo.
        <div style={{ marginTop: 6 }}>
          <strong>Scadenza preventivo:</strong> {formatDecorrenza(data.created_at)}
        </div>
        <div style={{ marginTop: 4 }}>
          <strong>Data di decorrenza polizza:</strong>{' '}
          {data.dati_comuni?.data_decorrenza
            ? formatDataIt(new Date(data.dati_comuni.data_decorrenza))
            : '— (da selezionare nello step Documenti)'}
        </div>
      </div>

      {/* Consenso privacy obbligatorio prima dell'invio (GDPR Art. 6) */}
      <div className="card" style={{ marginTop: '1rem', borderColor: consensoPrivacy ? 'var(--green)' : 'var(--border-strong)', borderWidth: 2 }}>
        <label className="check-row" style={{ cursor: 'pointer', alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={consensoPrivacy}
            onChange={e => setConsensoPrivacy(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ marginLeft: 10 }}>
            <strong>Confermo che il contraente ha letto e accettato l'informativa privacy</strong>
            <div className="text-small text-muted" style={{ marginTop: 6, lineHeight: 1.5 }}>
              I dati personali del cliente ({data.cliente_cognome} {data.cliente_nome}), inclusi
              email, cellulare e dati del veicolo, saranno trattati dal broker
              <strong> Diemmeci Broker</strong> e dalla compagnia assicurativa scelta per le finalità
              di emissione e gestione della polizza CVT, in conformità al Regolamento UE 2016/679 (GDPR)
              e alla normativa IVASS. Base giuridica: esecuzione del contratto assicurativo (Art. 6.1.b GDPR).
              I dati saranno conservati per il periodo richiesto dalla normativa di settore.
              Il cliente può esercitare i diritti previsti dagli artt. 15-22 GDPR contattando il titolare del trattamento.
              <br /><br />
              <em>Spuntando questa casella confermi sotto la tua responsabilità di aver consegnato
              l'informativa privacy completa al cliente prima della raccolta dei dati.</em>
            </div>
          </span>
        </label>
      </div>

      <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '14px', marginTop: '1rem' }}
              onClick={inviaPratica}
              disabled={
                sending
                || !consensoPrivacy
                || (!isSoloStandalone && !data.scelta_compagnia)
                || (isSoloStandalone && !(data.standalone_selezionati?.length > 0))
              }>
        {sending ? 'Invio in corso...' : '✉ Invia pratica all\'amministrazione'}
      </button>
      {!consensoPrivacy && (
        <p className="text-small text-muted" style={{ textAlign: 'center', marginTop: 8 }}>
          Accetta l'informativa privacy per abilitare l'invio
        </p>
      )}
    </div>
  )
}
